import React, {
  Fragment, useEffect, useMemo, useRef, useState,
} from 'react';
import {useRouter} from 'next/router';
import moment from 'moment';
import {Transition} from '@headlessui/react';
import {XIcon} from '@heroicons/react/solid';

import Link from './Link';
import {BrandMark} from './Brand';
import {useAnalytics, useAppContext, useWindowSize} from '../hooks';
import {getCountryName} from '../util/countries';

const INITIAL_DELAY_MS = 31 * 1000;
const DISPLAY_MS = 9 * 1000;
const INTERVAL_MS = 46 * 1000;
const MAX_PER_PAGE = 3;
const DESKTOP_MIN_WIDTH = 768;
const HIDDEN_PATH_PREFIXES = ['/course', '/questions', '/practice'];

const DISMISSED_UNTIL_KEY = 'social_proof_dismissed_until';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

const UPGRADE_URL = '/upgrade?src=social_proof';
const YOUTUBE_URL = 'https://www.youtube.com/@aced_io?sub_confirmation=1';
const YOUTUBE_SUBSCRIBERS = '500,000+';
// Shown after this many purchases, so the first notifications are always live activity
const YOUTUBE_POSITION = 2;

const CountryPhrases = {US: 'the US', GB: 'the UK', AE: 'the UAE'};

const getLocation = (country) => CountryPhrases[country] || getCountryName(country);

const isDismissed = () => {
  try {
    return Number(localStorage.getItem(DISMISSED_UNTIL_KEY)) > Date.now();
  } catch (e) {
    return false;
  }
};

const dismissForADay = () => {
  try {
    localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + DISMISS_DURATION_MS));
  } catch (e) {
    // Storage unavailable: the notification still closes for this page
  }
};

const buildNotifications = (purchases) => {
  if (!purchases.length) return [];
  const purchaseNotifications = purchases.map((purchase) => ({type: 'purchase', ...purchase}));
  return [
    ...purchaseNotifications.slice(0, YOUTUBE_POSITION),
    {type: 'youtube'},
    ...purchaseNotifications.slice(YOUTUBE_POSITION),
  ];
};

const NotificationMessage = ({notification}) => {
  if (notification.type === 'youtube') {
    return (
      <p>
        <strong>{YOUTUBE_SUBSCRIBERS}</strong>
        {' people subscribe to our YouTube channel'}
      </p>
    );
  }

  const location = getLocation(notification.country);
  return (
    <>
      <p>
        <strong>{notification.firstName}</strong>
        {location && ` from ${location}`}
        {' just purchased a membership'}
      </p>
      <p className="mt-0.5 text-xs text-gray-500">{moment(notification.createdAt).fromNow()}</p>
    </>
  );
};

const SocialProofNotifications = () => {
  const {loading, hasSubscription, recentPurchases} = useAppContext();
  const {tracker} = useAnalytics();
  const {asPath} = useRouter();
  const {width} = useWindowSize();
  const nextIndex = useRef(0);
  const pageCount = useRef({path: null, count: 0});
  const [notification, setNotification] = useState(null);
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  const notifications = useMemo(() => buildNotifications(recentPurchases), [recentPurchases]);
  const isHiddenPath = HIDDEN_PATH_PREFIXES.some((prefix) => asPath.startsWith(prefix));
  const isDesktop = width >= DESKTOP_MIN_WIDTH;
  const enabled = !loading && !hasSubscription && !dismissed && !isHiddenPath && isDesktop
    && notifications.length > 0;

  useEffect(() => {
    setDismissed(isDismissed());
  }, []);

  // Each page shows up to MAX_PER_PAGE, continuing where the last page left off.
  // The count is kept per path so crossing the breakpoint doesn't reset it.
  useEffect(() => {
    if (!enabled) return undefined;
    if (pageCount.current.path !== asPath) pageCount.current = {path: asPath, count: 0};

    let timer;
    const showNext = () => {
      const isExhausted = nextIndex.current >= notifications.length;
      if (isExhausted || pageCount.current.count >= MAX_PER_PAGE) return;
      setNotification(notifications[nextIndex.current]);
      setShow(true);
      nextIndex.current += 1;
      pageCount.current.count += 1;

      timer = setTimeout(() => {
        setShow(false);
        if (pageCount.current.count < MAX_PER_PAGE) timer = setTimeout(showNext, INTERVAL_MS);
      }, DISPLAY_MS);
    };
    timer = setTimeout(showNext, INITIAL_DELAY_MS);

    return () => {
      clearTimeout(timer);
      setShow(false);
    };
  }, [enabled, asPath, notifications]);

  if (!notification) return null;

  const onClick = () => tracker.socialProofClick({type: notification.type});
  const onDismiss = () => {
    dismissForADay();
    setDismissed(true);
    tracker.socialProofDismiss({type: notification.type});
  };

  return (
    <div aria-live="polite" className="fixed bottom-5 left-5 z-20 pointer-events-none">
      <Transition
        show={show}
        as={Fragment}
        enter="transform ease-out duration-500 transition"
        enterFrom="translate-y-full opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transform ease-in duration-500 transition"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-full opacity-0"
      >
        <div className="relative w-80 bg-white shadow-lg rounded-lg ring-1 ring-black/5 pointer-events-auto">
          <Link
            href={notification.type === 'youtube' ? YOUTUBE_URL : UPGRADE_URL}
            onClick={onClick}
            className="flex items-center gap-3 p-3 pr-8 text-sm text-gray-900 hover:bg-gray-50 rounded-lg"
          >
            <BrandMark className="h-10 w-10" />
            <div>
              <NotificationMessage notification={notification} />
            </div>
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            className="absolute top-2 right-2 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <span className="sr-only">Hide notifications for a day</span>
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </Transition>
    </div>
  );
};

export default SocialProofNotifications;
